import { API_BASE_URL } from "@/config";

export default {
  name: "QualifyingCriteria",
  props: {
    getSavedSectionResponses: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      // Form fields
      credit_check_consent: "",
      client_insolvent_ind: "",
      client_admin_order_ind: "",
      client_sequestrian_order_ind: "",
      client_debt_review_ind: "",
      client_guarantor_ind: "",
      client_surety_ind: "",
      client_co_debtor_ind: "",
      client_debt_rearrangement_ind: "",
      client_garnishee_order_ind: "",
      client_credit_dispute_ind: "",
      credit_rehabilitation_ind: "",

      // For API integration
      questions: [],
      question_map: {},
      error_messages: {},
    };
  },
  computed: {
    workflow_id() {
      return this.$store.getters.get_workflow_id;
    },
  },
  methods: {
    get_questions() {
      const TOKEN = localStorage.getItem("token");
      const GET_QUESTIONS_URL = `${API_BASE_URL}/verify_api/financial_information_api/get_qualifying_questions/`;

      $.ajax({
        url: GET_QUESTIONS_URL,
        method: "GET",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        beforeSend: () => {
          Swal.fire({
            text: "Fetching qualifying criteria questions...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });
        },
        success: (response) => {
          Swal.close();
          this.questions = response.questions;

          // Map each question's api_form_field to its id
          this.questions.forEach((question) => {
            if (question.api_form_field) {
              this.question_map[question.api_form_field] = question.id;
            }
          });
          this.load_saved_responses();
        },
        error: () => {
          Swal.close();
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to fetch qualifying criteria questions. Please try again later.",
            confirmButtonText: "OK",
          });
        },
      });
    },
    validate_declarations_form(section = null) {
      this.error_messages = {}; // Reset errors

      if (section && Array.isArray(section.questions)) {
        section.questions.forEach((question) => {
          const field_name = question.api_form_field;
          if (!field_name) return;
          
          if (question.required && !question.response) {
            this.error_messages[field_name] = "This field is required.";
          }
          
          if (field_name === 'credit_check_consent' && !question.response) {
            this.error_messages.credit_check_consent = "You must consent to the credit check.";
          }
        });
        return Object.keys(this.error_messages).length === 0;
      }

      // Validate each field based on question requirements
      this.questions.forEach((question) => {
        const field_name = question.api_form_field;
        if (!field_name) return; // Skip if no field mapping

        // Check if field is required and empty
        if (question.required && !this[field_name]) {
          this.error_messages[field_name] = "This field is required.";
        }
      });

      if (!this.credit_check_consent) {
        this.error_messages.credit_check_consent =
          "You must consent to the credit check.";
      }
      return Object.keys(this.error_messages).length === 0;
    },
    submit_declarations(section = null, customer_id = null) {
      // Handle case where section is a DOM event (from template click)
      if (
        section &&
        (section instanceof Event || section.type === "click" || section.target)
      ) {
        section = null;
      }

      if (!section && !this.validate_declarations_form()) {
        console.error("Validation errors:", this.error_messages);
        Swal.fire({
          icon: "warning",
          title: "Incomplete Form",
          text: "Please complete all required fields before continuing.",
          confirmButtonText: "OK",
        });
        return;
      }

      if (section && !this.validate_declarations_form(section)) {
        console.error("Validation errors:", this.error_messages);
        Swal.fire({
          icon: "warning",
          title: "Incomplete Form",
          text: "Please complete all required fields before continuing.",
          confirmButtonText: "OK",
        });
        return;
      }

      const TOKEN = localStorage.getItem("token");
      const SAVE_RESPONSES_URL = `${API_BASE_URL}/verify_api/financial_information_api/save_qualifying_criteria/`;

      // Prepare responses by mapping form values back to question_ids
      let responses = [];

      if (section && Array.isArray(section.questions)) {
        responses = section.questions.map((q) => ({
          question_id: q.question_id,
          answer: q.response?.toString() || "",
        }));
      } else {
        // Process each field in our data model
        Object.keys(this.$data).forEach((field_name) => {
          // Skip non-form fields or empty values
          if (
            !this.question_map[field_name] ||
            field_name === "questions" ||
            field_name === "question_map" ||
            field_name === "error_messages"
          ) {
            return;
          }

          // Add to responses array - ensure boolean values are converted to strings
          let answer_value = this[field_name];
          if (typeof answer_value === "boolean") {
            answer_value = answer_value ? "YES" : "NO";
          }

          responses.push({
            question_id: this.question_map[field_name],
            answer: answer_value || "",
          });
        });
      }

      const FORM_DATA = {
        workflow_id: this.workflow_id,
        responses: responses,
      };

      if (customer_id) {
        FORM_DATA.customer_id = customer_id;
      }

      $.ajax({
        url: SAVE_RESPONSES_URL,
        method: "POST",
        contentType: "application/json",
        headers: {
          Authorization: `Token ${TOKEN}`,
        },
        data: JSON.stringify(FORM_DATA),
        beforeSend: () => {
          Swal.fire({
            text: "Saving qualifying criteria...",
            allowEscapeKey: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
          });
        },
        success: () => {
          Swal.close();
          if (section && customer_id) {
            Swal.fire({
              icon: "success",
              title: "Success",
              text: "Qualifying criteria changed successfully!",
              timer: 2000,
              showConfirmButton: false,
            });
          } else {
            this.$emit("validity-change", true);
          }
        },
        error: (xhr) => {
          Swal.close();
          let errorMessage =
            "Failed to save responses. Please try again later.";
          if (xhr.responseJSON && xhr.responseJSON.error) {
            errorMessage = xhr.responseJSON.error;
          }
          Swal.fire({
            icon: "error",
            title: "Error",
            text: errorMessage,
            confirmButtonText: "OK",
          });
        },
      });
    },
    async load_saved_responses() {
      try {
        const responses = await this.getSavedSectionResponses(
          "Qualifying Criteria"
        );

        const idToField = {};
        Object.entries(this.question_map).forEach(([field, qid]) => {
          idToField[qid] = field;
        });

        if (Array.isArray(responses) && responses.length > 0) {
          responses.forEach((resp) => {
            const field = idToField[resp.question_id];
            if (field && Object.prototype.hasOwnProperty.call(this, field)) {
              this[field] = resp.answer;
            }
          });
        }
      } catch (error) {
        console.error("Error loading saved responses:", error);
      }
    },
  },
  mounted() {
    if (this.$store.getters.get_user_role?.toLowerCase() === "customer") {
      this.get_questions();
    }
  },
};
